<template>
  <!-- A folha do exercicio (card 17trt6jcyfb). Um componente para os tres
       usos, para o idioma visual ser um so:
       - answer: o aluno preenche (o roxo e o que ele escreve);
       - review: a folha corrigida, com a caneta rosa do professor;
       - grade: quem corrige ajusta cada questao na propria folha;
       - key: o professor consulta o gabarito na aula, sem resposta de aluno
         (retorno de 18/10). -->

  <ol class="xsheet" :class="`xsheet--${mode}`">
    <li
      v-for="(item, index) in exercise.items"
      :key="item.id"
      class="xq"
      :class="questionClass(item)"
    >
      <span class="xq__letter" aria-hidden="true">{{
        marginLetter(item, index)
      }}</span>

      <div class="xq__body">
        <exercise-media :media="item.content.media" compact />

        <!-- Alternativa -->
        <template v-if="exercise.type === 'MULTIPLE_CHOICE'">
          <p v-if="item.content.text" class="xq__text">
            {{ stripLetter(item.content.text) }}
          </p>
          <div
            class="xopts"
            role="radiogroup"
            :aria-label="$t('sheet.question', { letter: letter(index) })"
          >
            <button
              v-for="option in item.content.options"
              :key="option.id"
              type="button"
              role="radio"
              class="xopt"
              :class="optionClass(item, option)"
              :aria-checked="String(chosenOption(item) === option.id)"
              :disabled="!answering"
              @click="chooseOption(item, option.id)"
            >
              <exercise-media :media="option.media" compact />
              <span class="xopt__text">{{ option.text }}</span>
              <span
                v-if="
                  !answering &&
                  isKeyOption(item, option) &&
                  !isChosenRight(item)
                "
                class="pen xopt__pen"
                >{{ $t("sheet.thisOne") }}</span
              >
            </button>
          </div>
        </template>

        <!-- Ordenar frase ("unscramble", retorno de 16/10) -->
        <template v-else-if="exercise.type === 'ORDER_WORDS'">
          <order-words
            v-if="answering"
            :tokens="item.content.tokens || []"
            :value="responseOf(item).sequence || []"
            :letter="letter(index)"
            @input="emit(item, { sequence: $event })"
          />
          <div v-else class="xline" :class="orderClass(item)">
            <span v-if="keyOnly" class="xline__tokens">{{
              (item.content.tokens || []).join(" / ")
            }}</span>
            <span v-else class="xline__given">{{
              (responseOf(item).sequence || []).join(" ") ||
              $t("sheet.noAnswer")
            }}</span>
            <span
              v-if="
                item.answerKey &&
                (keyOnly || (item.result && !item.result.correct))
              "
              class="pen xline__pen"
            >
              {{ (item.answerKey.sequence || []).join(" ") }}
            </span>
          </div>
        </template>

        <!-- Lista suspensa e completar texto: texto com lacunas -->
        <p v-else-if="isSentenceAnswer(item)" class="xq__text">
          {{ stripLetter(item.content.parts[0]) }}
        </p>
        <p v-else class="xq__text xq__text--flow">
          <template v-for="(part, blank) in item.content.parts">
            <span :key="`t${blank}`">{{
              blank === 0 ? stripLetter(part) : part
            }}</span>
            <span
              v-if="blank < item.content.parts.length - 1"
              :key="`b${blank}`"
              class="xslot"
              :class="blankClass(item, blank)"
            >
              <template v-if="answering">
                <select
                  v-if="exercise.type === 'DROPDOWN'"
                  class="xslot__select"
                  :value="blankValue(item, blank)"
                  :aria-label="
                    $t('sheet.blank', { n: blank + 1, letter: letter(index) })
                  "
                  @change="setBlank(item, blank, $event.target.value)"
                >
                  <option value="">…</option>
                  <option
                    v-for="option in item.content.options"
                    :key="option.id"
                    :value="option.id"
                  >
                    {{ option.text }}
                  </option>
                </select>
                <input
                  v-else
                  class="xslot__input"
                  type="text"
                  autocomplete="off"
                  autocapitalize="off"
                  spellcheck="false"
                  :size="inputSize(item, blank)"
                  :value="blankValue(item, blank)"
                  :aria-label="
                    $t('sheet.blank', { n: blank + 1, letter: letter(index) })
                  "
                  @input="setBlank(item, blank, $event.target.value)"
                />
              </template>
              <template v-else>
                <span v-if="!keyOnly" class="xslot__given">{{
                  givenText(item, blank) || "—"
                }}</span>
                <span
                  v-if="showPen(item, blank)"
                  class="pen xslot__pen"
                  :class="penClass(item, blank)"
                >
                  {{ expectedText(item, blank) }}
                </span>
              </template>
            </span>
          </template>
        </p>

        <!-- Traducao / frase inteira: uma linha propria, larga -->
        <div
          v-if="isSentenceAnswer(item)"
          class="xline"
          :class="blankClass(item, 0)"
        >
          <input
            v-if="answering"
            class="xline__input"
            type="text"
            autocomplete="off"
            spellcheck="false"
            :placeholder="$t('sheet.writeSentence')"
            :value="blankValue(item, 0)"
            :aria-label="$t('sheet.answerOf', { letter: letter(index) })"
            @input="setBlank(item, 0, $event.target.value)"
          />
          <template v-else>
            <span v-if="!keyOnly" class="xline__given">{{
              givenText(item, 0) || $t("sheet.noAnswer")
            }}</span>
            <span
              v-if="showPen(item, 0)"
              class="pen xline__pen"
              :class="penClass(item, 0)"
            >
              {{ expectedText(item, 0) }}
            </span>
          </template>
        </div>

        <!-- Situacao da questao depois de corrigida -->
        <div v-if="!answering && item.result" class="xq__foot">
          <span v-if="item.result.overridden" class="xnote xnote--pen">
            {{ $t("sheet.overridden") }}
          </span>
          <span
            v-else-if="item.result.acceptedWithTolerance"
            class="xnote xnote--amber"
            :title="$t('sheet.toleranceTitle')"
          >
            {{ $t("sheet.tolerance") }}
          </span>
          <span v-else-if="!item.result.answered" class="xnote">
            {{ $t("sheet.blankAnswer") }}
          </span>

          <div
            v-if="mode === 'grade'"
            class="xgrade"
            role="group"
            :aria-label="$t('sheet.gradeOf', { letter: letter(index) })"
          >
            <button
              type="button"
              class="xgrade__btn xgrade__btn--right"
              :class="{ 'is-on': item.result.correct }"
              :aria-pressed="String(item.result.correct)"
              :disabled="busyItem === item.id"
              @click="$emit('correct', item.id, true)"
            >
              <i class="mdi mdi-check" aria-hidden="true"></i>
              {{ $t("sheet.right") }}
            </button>
            <button
              type="button"
              class="xgrade__btn xgrade__btn--wrong"
              :class="{ 'is-on': !item.result.correct }"
              :aria-pressed="String(!item.result.correct)"
              :disabled="busyItem === item.id"
              @click="$emit('correct', item.id, false)"
            >
              <i class="mdi mdi-close" aria-hidden="true"></i>
              {{ $t("sheet.wrong") }}
            </button>
            <button
              v-if="item.result.overridden"
              type="button"
              class="xgrade__undo"
              :disabled="busyItem === item.id"
              @click="$emit('correct', item.id, null)"
            >
              {{ $t("sheet.undo") }}
            </button>
          </div>
        </div>
      </div>

      <span
        v-if="!answering && item.result"
        class="xq__mark"
        :class="item.result.correct ? 'xq__mark--right' : 'xq__mark--wrong'"
        :aria-label="
          item.result.correct ? $t('sheet.right') : $t('sheet.wrong')
        "
      >
        <svg v-if="item.result.correct" viewBox="0 0 32 32" aria-hidden="true">
          <path d="M6 17 L13 24 L27 7" />
        </svg>
        <svg v-else viewBox="0 0 32 32" aria-hidden="true">
          <path d="M8 8 L24 24 M24 8 L8 24" />
        </svg>
      </span>
    </li>
  </ol>
</template>

<script>
import ExerciseMedia from "./ExerciseMedia.vue";
import OrderWords from "./OrderWords.vue";

export default {
  name: "ExerciseSheet",
  components: { ExerciseMedia, OrderWords },

  props: {
    exercise: { type: Object, required: true },
    // answer | review | grade
    mode: { type: String, default: "answer" },
    // Respostas em andamento, por id da questao (so no modo answer).
    value: { type: Object, default: () => ({}) },
    busyItem: { type: String, default: null },
  },

  computed: {
    answering() {
      return this.mode === "answer";
    },
    keyOnly() {
      return this.mode === "key";
    },
  },

  methods: {
    letter(index) {
      return String.fromCharCode(97 + index);
    },

    // Os questionarios antigos ja escrevem "a) " no comeco do texto. A letra
    // vai para a margem e sai do texto, para nao aparecer duas vezes.
    ownLetter(item) {
      const head =
        (item.content.parts && item.content.parts[0]) ||
        item.content.text ||
        "";
      const match = /^\s*([a-z])\)\s*/i.exec(head);
      return match ? match[1].toLowerCase() : null;
    },

    marginLetter(item, index) {
      return this.ownLetter(item) || this.letter(index);
    },

    stripLetter(text) {
      return String(text || "").replace(/^\s*[a-z]\)\s*/i, "");
    },

    // --- respostas ---------------------------------------------------------
    responseOf(item) {
      return this.answering ? this.value[item.id] || {} : item.response || {};
    },

    emit(item, response) {
      this.$emit("input", { ...this.value, [item.id]: response });
    },

    chosenOption(item) {
      return this.responseOf(item).optionId || null;
    },

    chooseOption(item, optionId) {
      if (!this.answering) return;
      this.emit(item, { optionId });
    },

    blankKey() {
      return this.exercise.type === "DROPDOWN" ? "optionIds" : "values";
    },

    blankValue(item, blank) {
      return (this.responseOf(item)[this.blankKey()] || [])[blank] || "";
    },

    setBlank(item, blank, raw) {
      const key = this.blankKey();
      const count = Math.max(1, (item.content.parts || []).length - 1);
      const current = [...(this.responseOf(item)[key] || [])];
      while (current.length < count) current.push("");
      current[blank] = raw;
      this.emit(item, { [key]: current });
    },

    // Traducao: texto sem lacuna marcada, a resposta e a frase inteira.
    isSentenceAnswer(item) {
      const parts = item.content.parts || [];
      return (
        this.exercise.type === "FILL_TEXT" &&
        parts.length === 2 &&
        !parts[1].trim()
      );
    },

    inputSize(item, blank) {
      const typed = this.blankValue(item, blank).length;
      return Math.min(Math.max(typed + 1, 7), 28);
    },

    // --- revisao -----------------------------------------------------------
    optionText(item, optionId) {
      const option = (item.content.options || []).find(
        (o) => o.id === optionId
      );
      return option ? option.text : "";
    },

    givenText(item, blank) {
      const value = this.blankValue(item, blank);
      return this.exercise.type === "DROPDOWN"
        ? this.optionText(item, value)
        : value;
    },

    blankResult(item, blank) {
      return (
        (item.result && item.result.blanks && item.result.blanks[blank]) || null
      );
    },

    // Caneta so onde a lacuna errou e ha gabarito para mostrar. Questao
    // validada pelo professor nao ganha correcao por cima.
    showPen(item, blank) {
      if (this.keyOnly) return !!item.answerKey;
      if (!item.result || !item.answerKey) return false;
      const result = this.blankResult(item, blank);
      // Quem corrige ve o esperado tambem onde a digitacao foi tolerada,
      // para decidir se aceita.
      if (this.mode === "grade" && result && result.tolerance) return true;
      if (item.result.correct) return false;
      return !result || !result.correct;
    },

    penClass(item, blank) {
      const result = this.blankResult(item, blank);
      return {
        "pen--amber":
          item.result && item.result.correct && result && result.tolerance,
      };
    },

    expectedText(item, blank) {
      const key = item.answerKey || {};
      if (this.exercise.type === "DROPDOWN") {
        return this.optionText(item, (key.blankOptionIds || [])[blank]);
      }
      const accepted = (key.blanks || [])[blank] || [];
      // No gabarito do professor vao todas as respostas aceitas (I'm / I am).
      return this.keyOnly ? accepted.join(" / ") : accepted[0] || "";
    },

    isKeyOption(item, option) {
      return !!item.answerKey && item.answerKey.optionId === option.id;
    },

    isChosenRight(item) {
      return !!item.result && item.result.correct;
    },

    // --- classes -----------------------------------------------------------
    questionClass(item) {
      if (this.answering || !item.result) return {};
      return {
        "xq--right": item.result.correct,
        "xq--wrong": !item.result.correct,
      };
    },

    optionClass(item, option) {
      const chosen = this.chosenOption(item) === option.id;
      if (this.answering) return { "is-chosen": chosen };
      const right = this.isChosenRight(item);
      return {
        "is-chosen": chosen,
        "is-right": chosen && right,
        "is-wrong": chosen && !right,
        "is-key": this.isKeyOption(item, option) && !right,
      };
    },

    orderClass(item) {
      if (!item.result) return {};
      return {
        "is-right": item.result.correct,
        "is-wrong": !item.result.correct,
      };
    },

    blankClass(item, blank) {
      if (this.answering || !item.result) {
        return { "is-filled": !!this.blankValue(item, blank) };
      }
      const result = this.blankResult(item, blank);
      const right = item.result.correct || (result && result.correct);
      return {
        "is-right": right,
        "is-wrong": !right,
        "is-tolerant": result && result.tolerance,
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.xsheet {
  --ink: var(--dk-ink, #2b2540);
  --muted: var(--dk-muted, #7a7590);
  --rule: var(--dk-line, #ece6f5);
  /* Retorno de 16/10: o que o aluno escreve fica na cor do aluno (rosa) e a
     correcao, a "caneta do professor", na cor do professor (roxo). */
  --fill: var(--dk-pink, #e8276f);
  --fill-wash: var(--dk-pink-wash, #fdeef4);
  --pen: var(--dk-grape, #a826e1);
  --right: var(--dk-teal, #00a379);
  --amber: var(--dk-ember-deep, #b7791f);

  list-style: none;
  margin: 0;
  padding: 0;
  counter-reset: none;
}

/* --- questao ---------------------------------------------------------- */
.xq {
  position: relative;
  display: grid;
  grid-template-columns: 28px 1fr 36px;
  gap: 4px 12px;
  padding: 22px 0;
  border-top: 1px dashed var(--rule);
}

.xq:first-child {
  border-top: 0;
}

.xq__letter {
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 16px;
  line-height: 1.6;
  color: var(--muted);
}

.xq__body {
  min-width: 0;
}

.xq__text {
  margin: 0;
  font-size: 17px;
  line-height: 2.1;
  color: var(--ink);
}

.xq__text--flow {
  line-height: 2.6;
}

/* --- lacuna ------------------------------------------------------------ */
.xslot {
  position: relative;
  display: inline-block;
  margin: 0 4px;
  vertical-align: baseline;
}

.xslot__input,
.xslot__select {
  font: inherit;
  font-weight: 600;
  color: var(--fill);
  background: transparent;
  border: 0;
  border-bottom: 2px solid var(--dk-line-strong, #cfc6df);
  border-radius: 0;
  padding: 0 4px 1px;
  min-width: 4.5em;
  outline: none;
  transition: border-color 0.15s ease, background-color 0.15s ease;
}

.xslot__select {
  appearance: none;
  padding-right: 18px;
  cursor: pointer;
  background-image: linear-gradient(45deg, transparent 50%, var(--muted) 50%),
    linear-gradient(135deg, var(--muted) 50%, transparent 50%);
  background-position: calc(100% - 9px) 55%, calc(100% - 5px) 55%;
  background-size: 4px 4px, 4px 4px;
  background-repeat: no-repeat;
}

.xslot__input:focus,
.xslot__select:focus-visible {
  border-bottom-color: var(--fill);
  background-color: var(--fill-wash);
}

.xslot.is-filled .xslot__input,
.xslot.is-filled .xslot__select {
  border-bottom-color: var(--fill);
}

.xslot__given {
  font-weight: 600;
  padding: 0 4px;
  border-bottom: 2px solid currentColor;
}

.xslot.is-right .xslot__given {
  color: var(--right);
}

.xslot.is-wrong .xslot__given {
  color: var(--ink);
  text-decoration: line-through;
  text-decoration-color: var(--pen);
  text-decoration-thickness: 2px;
  border-bottom-color: transparent;
}

.xslot.is-tolerant .xslot__given {
  border-bottom-style: dotted;
  border-bottom-color: var(--amber);
}

/* A caneta: a resposta certa escrita a mao, acima da errada. */
/* A correcao do professor: Poppins italica, na cor do professor. */
.pen {
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-style: italic;
  color: var(--pen);
  line-height: 1.2;
}

.pen--amber {
  color: var(--amber);
}

.xslot__pen {
  position: absolute;
  left: 50%;
  bottom: calc(100% - 6px);
  transform: translateX(-50%);
  font-size: 13px;
  white-space: nowrap;
  pointer-events: none;
}

/* Gabarito do professor: sem resposta de aluno embaixo, a caneta ocupa a
   propria lacuna, sublinhada como uma lacuna preenchida. */
.xsheet--key .xslot__pen {
  position: static;
  transform: none;
  font-size: inherit;
  white-space: normal;
  padding: 0 2px;
  border-bottom: 2px solid var(--rule, var(--dk-line, #ece6f5));
}

.xline__tokens {
  display: block;
  padding: 6px 2px 0;
  font-size: 15px;
  color: var(--dk-muted, #7a7590);
}

/* --- frase inteira (traducao) ----------------------------------------- */
.xline {
  position: relative;
  margin-top: 6px;
}

.xline__input {
  width: 100%;
  font: inherit;
  font-size: 17px;
  font-weight: 600;
  color: var(--fill);
  background: transparent;
  border: 0;
  border-bottom: 2px solid var(--dk-line-strong, #cfc6df);
  padding: 6px 2px;
  outline: none;
}

.xline__input:focus {
  border-bottom-color: var(--fill);
  background: var(--fill-wash);
}

.xline__input::placeholder {
  color: #b9b2c8;
  font-weight: 400;
}

.xline__given {
  display: block;
  font-size: 17px;
  font-weight: 600;
  padding: 6px 2px;
}

.xline.is-right .xline__given {
  color: var(--right);
}

.xline.is-wrong .xline__given {
  text-decoration: line-through;
  text-decoration-color: var(--pen);
  text-decoration-thickness: 2px;
}

.xline__pen {
  display: block;
  font-size: 15px;
  padding: 4px 2px 0;
}

/* --- alternativas -------------------------------------------------------- */
.xopts {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 10px;
}

.xopt {
  position: relative;
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-height: 44px;
  padding: 9px 18px;
  border: 1.5px solid var(--dk-line-strong, #ded7ea);
  border-radius: 999px;
  background: var(--dk-surface, #fff);
  font: inherit;
  font-size: 15.5px;
  font-weight: 500;
  color: var(--ink);
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease,
    color 0.15s ease;
}

.xopt:not(:disabled):hover {
  border-color: var(--fill);
}

.xopt:focus-visible {
  outline: 3px solid rgba(232, 39, 111, 0.35);
  outline-offset: 2px;
}

.xopt.is-chosen {
  border-color: var(--fill);
  background: var(--fill-wash);
  color: var(--fill);
  font-weight: 600;
}

.xopt:disabled {
  cursor: default;
}

.xopt.is-right {
  border-color: var(--right);
  background: var(--dk-teal-wash, #e6f7f1);
  color: var(--right);
}

.xopt.is-wrong {
  border-color: #f2c1d3;
  background: var(--dk-surface, #fff);
  color: var(--ink);
}

.xopt.is-wrong .xopt__text {
  text-decoration: line-through;
  text-decoration-color: var(--pen);
  text-decoration-thickness: 2px;
}

/* A alternativa certa, que o aluno nao marcou, circulada a caneta. */
.xopt.is-key {
  border: 2px solid var(--pen);
  border-radius: 46% 54% 50% 50% / 60% 48% 52% 40%;
}

.xopt__pen {
  position: absolute;
  top: -10px;
  right: -6px;
  padding: 0 6px;
  border-radius: 999px;
  background: var(--dk-surface, #fff);
  font-size: 12px;
}

/* --- rodape da questao ---------------------------------------------------- */
.xq__foot {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 14px;
  margin-top: 10px;
}

.xnote {
  font-size: 12.5px;
  color: var(--muted);
}

.xnote--amber {
  color: var(--amber);
}

.xnote--pen {
  font-family: "Poppins", sans-serif;
  font-size: 12.5px;
  font-weight: 600;
  font-style: italic;
  color: var(--pen);
}

.xgrade {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-left: auto;
}

.xgrade__btn {
  min-height: 34px;
  padding: 4px 12px;
  border-radius: 999px;
  border: 1.5px solid var(--dk-line-strong, #ded7ea);
  background: var(--dk-surface, #fff);
  font: inherit;
  font-size: 13px;
  font-weight: 600;
  color: var(--muted);
  cursor: pointer;
}

.xgrade__btn:focus-visible,
.xgrade__undo:focus-visible {
  outline: 3px solid rgba(232, 39, 111, 0.35);
  outline-offset: 2px;
}

.xgrade__btn--right.is-on {
  border-color: var(--right);
  background: var(--dk-teal-wash, #e6f7f1);
  color: var(--right);
}

.xgrade__btn--wrong.is-on {
  border-color: var(--pen);
  background: #fdeaf1;
  color: var(--pen);
}

.xgrade__undo {
  border: 0;
  background: none;
  font: inherit;
  font-size: 12.5px;
  color: var(--muted);
  text-decoration: underline;
  cursor: pointer;
}

/* --- marca de certo/errado, a caneta, na margem ----------------------- */
.xq__mark {
  width: 32px;
  height: 32px;
  margin-top: 6px;
}

.xq__mark svg {
  width: 100%;
  height: 100%;
  fill: none;
  stroke-width: 3.4;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.xq__mark--right svg {
  stroke: var(--right);
}

.xq__mark--wrong svg {
  stroke: var(--pen);
}

@media (max-width: 600px) {
  .xq {
    grid-template-columns: 22px 1fr 26px;
    gap: 4px 8px;
  }

  .xq__text {
    font-size: 16px;
  }

  .xq__mark {
    width: 24px;
    height: 24px;
  }

  .xgrade {
    margin-left: 0;
  }
}
</style>
